import React from "react";
import ScrollReveal from "../Components/ScrollReveal/ScrollReveal";
import KineticText from "../Components/KineticText";
import { useParallax } from "../Hooks/useScrollReveal";

const ParallaxImg = ({ src, alt }) => {
  const ref = useParallax({ speed: 0.1 });
  return (
    <div className="overflow-hidden rounded-lg shadow-xl w-full h-full">
      <img ref={ref} src={src} alt={alt} className="w-full h-[120%] object-cover -mt-[10%]" />
    </div>
  );
};

const ScentedMarketing = () => {
  return (
    <div className="overflow-x-hidden">
      <ScrollReveal>
        <KineticText as="p" splitBy="char" className="mt-[120px] mb-[50px] text-black text-5xl text-center heading-font font-bold">
          SCENTED MARKETING
        </KineticText>
      </ScrollReveal>
      
      {/* Banner Section */}
      <ScrollReveal variant="clip" distance={0}>
        <div className="w-full h-[50vh] bg-[#6E276C] flex justify-center items-center">
          <KineticText as="p" splitBy="word" className="text-5xl text-white text-center px-6 heading-font">
            Creating Memorable Brand Experiences Through Scent
          </KineticText>
        </div>
      </ScrollReveal>

      {/* Intro Section */}
      <div className="w-[90%] lg:w-[70%] mx-auto my-16 flex flex-col lg:flex-row items-center gap-10">
        <ScrollReveal variant="clip" className="w-full lg:w-1/2 h-[300px] sm:h-[400px]">
          <ParallaxImg
            src="https://cdn-prod.medicalnewstoday.com/content/images/articles/319/319615/smoke-abstract-image.jpg"
            alt="Scent Influence"
          />
        </ScrollReveal>
        <ScrollReveal className="w-full lg:w-1/2 text-lg" stagger={0.15}>
          <KineticText as="p" splitBy="word" className="text-4xl font-bold mb-4 heading-font">
            The Power of Smell
          </KineticText>
          <p>
            A scent has the ability to influence behavior and trigger memories
            almost instantaneously. When smell is combined with other marketing
            cues, it can amplify a brand experience and establish a long-lasting
            connection with consumers.
          </p>
        </ScrollReveal>
      </div>

      {/* Brand Differentiation Section */}
      <div className="w-[90%] lg:w-[70%] mx-auto my-16 flex flex-col lg:flex-row-reverse items-center gap-10">
        <ScrollReveal variant="clip" className="w-full lg:w-1/2 h-[300px] sm:h-[400px]">
          <ParallaxImg
            src="https://media.licdn.com/dms/image/v2/D5612AQFmq7elW0R6Fg/article-cover_image-shrink_720_1280/article-cover_image-shrink_720_1280/0/1683798376483?e=2147483647&v=beta&t=J7l3OTDdq1PP6b4_Flw6lO29m3mU5hk-58WKQTEzego"
            alt="Brand Differentiation"
          />
        </ScrollReveal>
        <ScrollReveal className="w-full lg:w-1/2 text-lg" stagger={0.15}>
          <KineticText as="p" splitBy="word" className="text-4xl font-bold mb-4 heading-font">
            Stand Out with Scent Marketing
          </KineticText>
          <p>
            At Nextcare Fragrances, we enable you to differentiate your brand
            and increase loyalty with Scent Marketing. Imagine a wonderful
            fragrance – where did you smell it? Our goal is to tap into this
            potential for your company, increasing your bottom line and leaving
            a lasting impression on customers.
          </p>
        </ScrollReveal>
      </div>

      {/* Emotional Connection Section */}
      <div className="w-[90%] lg:w-[70%] mx-auto my-16 flex flex-col lg:flex-row items-center gap-10">
        <ScrollReveal variant="clip" className="w-full lg:w-1/2 h-[300px] sm:h-[400px]">
          <ParallaxImg
            src="https://fragrancescience.com/wp-content/uploads/7-SCIENCE-OF-SMELL2000.jpg"
            alt="Emotional Connection"
          />
        </ScrollReveal>
        <ScrollReveal className="w-full lg:w-1/2 text-lg" stagger={0.15}>
          <KineticText as="p" splitBy="word" className="text-4xl font-bold mb-4 heading-font">
            Building Emotional Connections
          </KineticText>
          <p>
            Scent marketing helps customers associate positive emotions with
            your brand. A subtle yet powerful strategy, it enhances customer
            experiences by adding a pleasant fragrance, making their interaction
            with your brand memorable.
          </p>
        </ScrollReveal>
      </div>

      {/* Customized Scent Solutions Section */}
      <div className="w-[90%] lg:w-[70%] mx-auto my-16 flex flex-col lg:flex-row-reverse items-center gap-10">
        <ScrollReveal variant="clip" className="w-full lg:w-1/2 h-[300px] sm:h-[400px]">
          <ParallaxImg
            src="https://www.goldn.com/images/small_Create_Custom_Scent_Beauty_Brand_Goldn_fae6a48cbe/small_Create_Custom_Scent_Beauty_Brand_Goldn_fae6a48cbe.png"
            alt="Custom Scent Solutions"
          />
        </ScrollReveal>
        <ScrollReveal className="w-full lg:w-1/2 text-lg" stagger={0.15}>
          <KineticText as="p" splitBy="word" className="text-4xl font-bold mb-4 heading-font">Tailored Scent Solutions</KineticText>
          <p>
            We collaborate with brand owners to design and blend unique
            fragrances. Our solutions include specialized systems for large
            spaces, integrated into your HVAC systems, as well as portable
            plug-and-play options for smaller spaces.
          </p>
        </ScrollReveal>
      </div>

      {/* Support and Maintenance Section */}
      <div className="w-[90%] lg:w-[70%] mx-auto my-16 flex flex-col lg:flex-row items-center gap-10">
        <ScrollReveal variant="clip" className="w-full lg:w-1/2 h-[300px] sm:h-[400px]">
          <ParallaxImg
            src="https://blog.mindgrub.com/hubfs/Importance%20of%20Ongoing%20Technical.jpg"
            alt="Support and Maintenance"
          />
        </ScrollReveal>
        <ScrollReveal className="w-full lg:w-1/2 text-lg" stagger={0.15}>
          <KineticText as="p" splitBy="word" className="text-4xl font-bold mb-4 heading-font">
            Ongoing Support and Maintenance
          </KineticText>
          <p>
            Our technical team provides seamless installation and reliable
            maintenance services. With monthly or bimonthly visits for fragrance
            replenishment, you’ll receive all the support needed to keep your
            scent marketing on point.
          </p>
        </ScrollReveal>
      </div>
    </div>
  );
};

export default ScentedMarketing;
